<script setup>
import { computed, ref, watch } from 'vue';
import { createToaster } from '@meforma/vue-toaster';
import ModalBase from '@/components/utils/ModalBase.vue';
import ModalBody from '@/components/utils/ModalBody.vue';
import DropArea from '../utils/DropArea.vue';
import FilesTable from '../files/FilesTable.vue';
import { useFilesHandler } from '../../composables/filesHandler';
import { v4 as uuidv4 } from 'uuid';
import repositoryGeneric from '../../data/repositoryGeneric';
import { handleResponseWithMessages } from '../../helpers/wwwHelper';
import { TOASTER_ATTACH_FILE_MSG } from '../../data/constants/toasterMessagesConstants';
import Swal from 'sweetalert2';

const props = defineProps({
  orderId: { type: Number, required: true }
});

const toaster = createToaster({ position: 'top-right', duration: 5000 });
const FILE_INPUT_ID = uuidv4();
const acceptedFileTypes = [
  '.xlsx',
  '.xls',
  '.csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv'
];

const emit = defineEmits(['importedAndConfirmed']);

const modalBase = ref();
const formRef = ref();
const isLoading = ref(false);

const { filesRef, onFilesChanged, onDropAreaFilesChanged, clearFileInput } = useFilesHandler(
  false,
  FILE_INPUT_ID,
  acceptedFileTypes
);

const filesTable = ref([]);

const showInput = computed(() => filesTable.value.length === 0);

watch(filesRef, () => {
  recomputeFiles();
});

function recomputeFiles() {
  filesTable.value = filesTable.value.concat(filesRef.value);

  for (let i = 0; i < filesTable.value.length; i++) {
    filesTable.value[i].id = i;
  }
}

function show() {
  modalBase.value.show();
}

async function onSubmitBtnClicked() {
  if (filesTable.value.length === 0) {
    toaster.show(TOASTER_ATTACH_FILE_MSG, { type: 'warning' });
    return;
  }

  isLoading.value = true;
  const formData = new FormData();

  formData.append('attachment_6', filesTable.value[0]?.file);

  const response = await repositoryGeneric.fetchPostAndQueryParamsWithFormData(
    `order/${props.orderId}/ImportZal6File`,
    null,
    formData
  );

  let message = '';
  message += `<div style="font-size: 16">Zaktualizowano informacje o punktach: <b>${response?.updated_points}</b><br/>`;
  message += `Nie znaleziono punktów: <b>${response?.not_found_points}</b><br/>`;

  handleResponseWithMessages(response, response?.message, () => {
    Swal.fire({
      icon: 'info',
      title: 'Wynik',
      html: message,
      confirmButtonText: 'Odśwież dane'
    }).then((result) => {
      if (result.value) {
        emit('importedAndConfirmed');
      }
    });

    modalBase.value.hide();
  });

  isLoading.value = false;
}

function onCloseBtnClicked() {
  modalBase.value.hide();
}

function cleanUp() {
  clearFileInput();
  filesTable.value = [];
}

function submitExternal() {
  formRef.value?.requestSubmit();
}

function onFileDeleted(id) {
  filesTable.value = filesTable.value.filter((x) => x.id !== id);
}

defineExpose({ show });
</script>

<template>
  <ModalBase ref="modalBase" @closed="cleanUp">
    <ModalBody>
      <template v-slot:title>Import załącznika 6a</template>

      <template v-slot:body v-if="modalBase?.isOpen">
        <div class="ibox">
          <div class="ibox-content" :class="{ 'sk-loading': isLoading }" style="border-style: none">
            <div class="sk-spinner sk-spinner-three-bounce">
              <div class="sk-bounce1"></div>
              <div class="sk-bounce2"></div>
              <div class="sk-bounce3"></div>
            </div>

            <form ref="formRef" @submit.prevent="onSubmitBtnClicked">
              <h4>Wczytaj załącznik</h4>

              <template v-if="showInput">
                <DropArea
                  id="importAttachment6ADropArea"
                  class="dropArea"
                  caption="Upuść plik tutaj albo kliknij 'Wybierz plik' poniżej"
                  :fileInputId="FILE_INPUT_ID"
                  @onFileChanged="onDropAreaFilesChanged($event)"
                />
                <input
                  :id="FILE_INPUT_ID"
                  name="file"
                  type="file"
                  class="mt-2"
                  accept=".xlsx, .xls, .csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, text/csv"
                  @change="onFilesChanged($event)"
                />
              </template>

              <template v-else>
                <FilesTable
                  :show-header="false"
                  :filesArray="filesTable"
                  @fileDeleted="onFileDeleted"
                />
              </template>
            </form>
          </div>
        </div>
      </template>

      <template v-slot:footer>
        <button
          type="button"
          class="btn btn-outline btn-info"
          @click="onCloseBtnClicked"
          :disabled="isLoading"
        >
          Zamknij
        </button>
        <button
          type="button"
          @click="submitExternal"
          class="btn btn-info"
          :disabled="filesTable.length === 0 || isLoading"
        >
          Importuj
        </button>
      </template>
    </ModalBody>
  </ModalBase>
</template>

<style scoped></style>
